import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import systemSettingsService, {
  SystemFlagMap,
  SystemSettingKey,
} from '../../services/systemSettingsService';

interface State {
  flags: SystemFlagMap;
  loading: boolean;
  error: string | null;
}

const initialState: State = {
  flags: {
    'archive.edit.enabled': false,
    'archive.delete.enabled': false,
  },
  loading: false,
  error: null,
};

export const fetchSystemSettings = createAsyncThunk(
  'systemSettings/fetch',
  async (_: void, { rejectWithValue }) => {
    try {
      return await systemSettingsService.fetchAll();
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to load settings',
      );
    }
  },
);

export const updateSystemSetting = createAsyncThunk(
  'systemSettings/update',
  async (
    { key, value }: { key: SystemSettingKey; value: boolean },
    { rejectWithValue },
  ) => {
    try {
      await systemSettingsService.update(key, value);
      return { key, value };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          'Failed to update setting',
      );
    }
  },
);

const systemSettingsSlice = createSlice({
  name: 'systemSettings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.flags = { ...state.flags, ...action.payload };
      })
      .addCase(fetchSystemSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateSystemSetting.fulfilled, (state, action) => {
        state.flags[action.payload.key] = action.payload.value;
      })
      .addCase(updateSystemSetting.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default systemSettingsSlice.reducer;
